import React, { memo, useCallback } from 'react';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import { MoreHoriz as MoreHorizIcon } from '@material-ui/icons';
import RestoreIcon from '@material-ui/icons/Restore';
import { CircularProgress, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { FormattedMessage, useIntl } from 'react-intl';
import styles from './device-grid-styles';
import { Table } from '../../components/Table';
import { getBooleanString, getDeviceStatus } from '../../helper/translationFunctions';

type ColumnNames = 'name' | 'id' | 'tvAvailable' | 'PrivacyEnabled' | 'PrivacyTimeOut' | 'rebootStatus' | 'managedBy' | 'reset' | 'more' | 'action' | 'version';

export interface Device {
  userDeviceName: string;
  deviceName: string;
  id: string;
  tvAvailable?: boolean;
  PrivacyEnabled: boolean;
  PrivacyTimeOut: number;
  version: string;
  rebootStatus: 'ready' | 'device_ready' | 'unavailable' | 'device_powered_off' | 'rebooting' | 'device_rebooting' | 'ongoing' | 'device_ongoing';
  userId: {
    profile: {
      name: string;
    };
  };
}

interface Props extends WithStyles<typeof styles> {
  id: string;
  onResetClick?: (e, row: Device) => void;
  onOpenMoreClick?: (e, row: Device) => void;
  onDeleteDeviceClick?: (row: Device) => void;
  devices: Device[];
  isLoading: boolean;
  columnNames: ColumnNames[];
}

const DeviceGridComponent: React.FC<Props> = ({
  classes,
  columnNames,
  id,
  onResetClick,
  onOpenMoreClick,
  onDeleteDeviceClick,
  devices,
  isLoading,
}) => {
  const usedColumns = useColumns({ columnNames });
  const intl = useIntl();

  const renderTableRow = useCallback(() => {
    return devices.map((row) => ({
      name: row.userDeviceName || row.deviceName,
      id: row.id,
      tvAvailable: getBooleanString(intl, !!row.tvAvailable),
      PrivacyEnabled: getBooleanString(intl, !!row.PrivacyEnabled),
      PrivacyTimeOut: row.PrivacyTimeOut,
      version: row.version || 'Unknown',
      rebootStatus: getDeviceStatus(intl, row.rebootStatus),
      managedBy: row.userId?.profile?.name ?? '',
      reset: (
        <IconButton
          id="reset-in-devices"
          size="small"
          onClick={(e) => onResetClick(e, row)}
        >
          <RestoreIcon fontSize="inherit" color="primary" />
        </IconButton>
      ),
      more: (
        <IconButton
          id="more-option-in-devices"
          size="small"
          onClick={(e) => onOpenMoreClick(e, row)}
        >
          <MoreHorizIcon fontSize="inherit" color="primary" />
        </IconButton>
      ),
      action: (
        <IconButton
          aria-label={intl.formatMessage({ id: 'remove' })}
          color="inherit"
          size="small"
          onClick={() => onDeleteDeviceClick(row)}
        >
          <CloseIcon fontSize="inherit" />
        </IconButton>
      ),
    }));
  }, [devices, intl, onDeleteDeviceClick, onOpenMoreClick, onResetClick]);

  return (
    <Table
      customStyles={classes.tableWrapper}
      id={id}
      title={
        <div id="total-devices">
          <FormattedMessage
            id="total_devices"
            values={{ n: devices.length }}
          />
          {isLoading && (
            <CircularProgress
              size={24}
              style={{ marginLeft: 15, position: 'relative', top: 4 }}
            />
          )}
        </div>
      }
      columns={usedColumns}
      data={renderTableRow()}
      pagination
      serverSide={false}
      filter
    />
  );
};

export const DeviceGrid = memo(withStyles(styles)(DeviceGridComponent));

const useColumns = ({ columnNames }: Pick<Props, 'columnNames'>) => {
  const intl = useIntl();

  const availableColumns = [
    {
      name: 'name',
      label: intl.formatMessage({
        id: 'name',
      }),
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'id',
      label: intl.formatMessage({
        id: 'device_id',
      }),
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'tvAvailable',
      label: intl.formatMessage({
        id: 'tvavailable',
      }),
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'PrivacyEnabled',
      label: intl.formatMessage({
        id: 'privacy_enabled',
      }),
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'PrivacyTimeOut',
      label: intl.formatMessage({
        id: 'privacy_timeout',
      }),
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'rebootStatus',
      label: intl.formatMessage({
        id: 'reboot_status',
      }),
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'managedBy',
      label: intl.formatMessage({
        id: 'managed_by',
      }),
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'version',
      label: intl.formatMessage({
        id: 'software_version',
      }),
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'reset',
      label: intl.formatMessage({
        id: 'reset',
      }),
      options: {
        filter: false,
        sort: false,
      },
    },
    {
      name: 'more',
      label: intl.formatMessage({
        id: 'more',
      }),
      options: {
        filter: false,
        sort: false,
      },
    },
    {
      name: 'action',
      label: intl.formatMessage({
        id: 'action',
      }),
      options: {
        filter: false,
        sort: false,
      },
    },
  ];

  const result = columnNames.map((colName) => {
    const found = availableColumns.find(col => col.name === colName);
    return { ...found };
  });

  return result;
};
